import {
  MODAL_UPGRADE_PLAN,
  SEO_EXPORT_AS_CODE,
  SEO_PREVIEW,
} from '@components/App/Modal/constants'
import type { ID } from '@octopus-do/octopus-project-core'
import { selectIsLoading } from '@store/loaders'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { SeoActions } from '@store/reducers/seo/actions'
import { seoSyncLoader } from '@store/reducers/seo/saga/syncFromUrl'
import type { AppState } from '@store/types'
import { openModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useProjectAccess } from '@/hooks'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import { SEO_TAGS_PERMISSION } from '@/permissions/project/definitions'
import { GlobalActions } from '@/store'

const EMPTY_URL_HINT = 'Set the Page URL first'
const RELATIVE_URL_HINT = 'Page URL must be a full address, like https://…'

const isAbsoluteUrl = (value: string) => /^https?:\/\//i.test(value)

const getSyncHint = (pageUrl: string) => {
  if (isAbsoluteUrl(pageUrl)) {
    return null
  }

  return pageUrl ? RELATIVE_URL_HINT : EMPTY_URL_HINT
}

export const useSidebarMenu = (nodeId: ID) => {
  const dispatch = useDispatch()

  const projectId = useSelector<AppState, ID | null>(
    (state) => state.manager.meta.id
  )
  const activeTabId = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.tabs.activeTab
  )

  const pageUrl = useSelector<AppState, string>(({ seoForm }) =>
    seoForm.url.trim()
  )

  const readOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )
  const { hasAccess } = useProjectAccess(SEO_TAGS_PERMISSION.EDIT)
  const syncing = useSelector(selectIsLoading(seoSyncLoader(nodeId)))
  const artboardDisabled = useArtBoardDisabled()

  // OCT-5177: синхронизируем по адресу страницы, поэтому синхронизировать
  // нечего, пока адрес не абсолютный — фетчить относительный путь некуда.
  // The synced tags are an edit, so not while the artboard is off either.
  const canSync = isAbsoluteUrl(pageUrl) && !artboardDisabled
  const syncHint = getSyncHint(pageUrl)

  const handleCopy = () => {
    const copyUrl = `${window.location.origin}/${projectId}/${activeTabId}/p/${nodeId}/seo-mode`

    dispatch(GlobalActions.copy({ value: copyUrl }))
  }

  const handleClickExport = () => {
    dispatch(openModal(SEO_EXPORT_AS_CODE))
  }

  const handleClickPreview = () => {
    dispatch(openModal(SEO_PREVIEW))
  }

  // Меню остаётся открытым, пока идёт запрос: пункт показывает лоадер.
  const handleClickSync = () => {
    if (!hasAccess || !canSync || !projectId || syncing) {
      return
    }

    sendGtmEvent(GtmTriggerName.SEO_IMPORT)
    dispatch(SeoActions.syncFromUrl({ id: nodeId, projectId, url: pageUrl }))
  }

  const handleBadgeClick = (
    e: React.MouseEvent<HTMLDivElement>,
    onCloseDropdown: () => void
  ) => {
    e.preventDefault()
    e.stopPropagation()

    dispatch(openModal(MODAL_UPGRADE_PLAN))
    onCloseDropdown()
  }

  const handleMenuClick = (onClick: () => void, closeDropdown?: () => void) => {
    if (!hasAccess) {
      return
    }

    onClick()
    closeDropdown?.()
  }

  return {
    readOnly,
    hasAccess,
    canSync,
    syncing,
    syncHint,
    handleCopy,
    handleClickExport,
    handleClickPreview,
    handleClickSync,
    handleBadgeClick,
    handleMenuClick,
  }
}
